"use client";

import { useEffect, useRef, useState } from "react";
import { useLenis } from "@/hooks/useLenis";
import { cn } from "@/lib/utils";

type Heading = { id: string; text: string };

/**
 * The contents rail: where you are in the article.
 *
 * It reads the DOM rather than the block list, so it cannot disagree with what is actually
 * rendered. Lenis drives the page from the wheel, so its event is the one to listen to when
 * it is running.
 */

/**
 * The contents rail. Desktop only: on a narrow screen it would cost more room than it saves,
 * and the article is a single linear read anyway.
 */
export function ArticleContents({ headings }: { headings: Heading[] }) {
  const [active, setActive] = useState(headings[0]?.id ?? "");
  const list = useRef<HTMLUListElement>(null);
  const lenis = useLenis();

  /**
   * Long articles have more headings than the rail can show, so it scrolls on its own. Follow
   * the active one into view, or on a fifty-heading article the highlight spends most of the
   * read clipped out of sight and the rail looks like it has stopped working.
   */
  useEffect(() => {
    const ul = list.current;
    if (!ul || !active) return;
    const item = ul.querySelector<HTMLElement>(`a[href="#${CSS.escape(active)}"]`)?.parentElement;
    if (!item) return;

    const pad = 48; // keep a row or two of context around it
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    if (top - pad < ul.scrollTop) ul.scrollTop = Math.max(0, top - pad);
    else if (bottom + pad > ul.scrollTop + ul.clientHeight) ul.scrollTop = bottom + pad - ul.clientHeight;
  }, [active]);

  useEffect(() => {
    if (!headings.length) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      // The heading nearest the top of the viewport that has not scrolled past it yet.
      let current = headings[0].id;
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && el.getBoundingClientRect().top <= 140) current = h.id;
      }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    lenis?.on("scroll", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      lenis?.off("scroll", onScroll);
    };
  }, [headings, lenis]);

  if (headings.length < 3) return null;

  const jump = (e: React.MouseEvent, id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    if (lenis) lenis.scrollTo(el, { offset: -110 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav aria-label="On this page" className="sticky top-[calc(var(--nav-h)+40px)]">
      <p className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-faint">On this page</p>
      <ul ref={list} className="no-scrollbar mt-4 max-h-[calc(100vh-220px)] space-y-0.5 overflow-y-auto" data-lenis-prevent="">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              onClick={(e) => jump(e, h.id)}
              className={cn(
                "block border-l py-2 pl-4 pr-1 text-[13px] leading-[1.5] transition-colors duration-300",
                active === h.id
                  ? "border-mint font-medium text-ink"
                  : "border-line text-muted hover:border-ink/20 hover:text-ink",
              )}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
